import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { glossary } from '@sorare/core/src/lib/glossary';
import { ClickMarketHomeSection_Target } from '@sorare/core/src/protos/events/so5/shared/events';

import { PlayerDrawer } from 'components/player/PlayerDrawer';
import { Token } from 'components/token/Token';
import type { CardResultsProps } from 'searchCards/AdvancedCardSearch/types';

import { SlideShow } from '../SlideShow';
import type {
  CardsShowQuery,
  CardsShowQueryVariables,
} from './__generated__/index.graphql';

const Row = styled(Horizontal).attrs({ gap: 2 })`
  min-height: 422px;
`;

const EmptyState = styled(Vertical)`
  display: flex;
`;

const TokenSkeleton = styled(Skeleton)`
  width: 100%;
  height: 100%;
`;

const TokenWrapper = styled(Vertical)`
  width: 167px;
  height: 100%;
  border-radius: var(--double-unit);
  flex-shrink: 0;
`;

const CARDS_SHOW_QUERY = gql`
  query CardsShowQuery(
    $slugs: [String!]!
    $googlePlayStoreCountryCode: String
    $onlyPrimary: Boolean = true
    $withCollectionPredictions: Boolean = false
    $isAndroidApp: Boolean = false
    $isIosApp: Boolean = false
  ) {
    anyCards(slugs: $slugs) {
      slug
      anyPlayer {
        slug
      }
      ...Token_anyCard
    }
  }
  ${Token.fragments.anyCard}
` as TypedDocumentNode<CardsShowQuery, CardsShowQueryVariables>;

type Card = CardsShowQuery['anyCards'][number];

type trackProps = {
  target: ClickMarketHomeSection_Target;
  playerSlug?: string;
  cardSlug?: string;
  offerId?: string;
};

type Props = {
  hits?: {
    slug: string;
  }[];
  loading: boolean;
  track?: (props: trackProps) => void;
} & CardResultsProps;

type CardOpened = ComponentProps<typeof PlayerDrawer>['cardOpened'];

export const CardsShow = ({ hits, loading: hitsLoading, track }: Props) => {
  const [cardOpened, setCardOpened] = useState<CardOpened>(null);
  const { googlePlayStoreCountryCode, isAndroidApp, isIosApp } =
    useIsMobileApp();
  const { data, loading: cardsLoading } = useQuery(CARDS_SHOW_QUERY, {
    variables: {
      slugs: hits?.map(hit => hit.slug) || [],
      googlePlayStoreCountryCode,
      isAndroidApp,
      isIosApp,
    },
    skip: !hits?.length,
  });

  if (!hitsLoading && !hits?.length) {
    return (
      <EmptyState>
        <FormattedMessage {...glossary.noResultsFound} />
      </EmptyState>
    );
  }

  const mappedCards = (data?.anyCards || []).reduce<{ [slug: string]: Card }>(
    (acc, card) => {
      acc[card.slug] = card;
      return acc;
    },
    {}
  );

  return (
    <SlideShow>
      <Row center={hitsLoading}>
        {!!hitsLoading && !hits?.length && <LoadingIndicator />}
        {hits?.map(h => {
          const item = mappedCards[h.slug];
          if (cardsLoading) {
            return (
              <TokenWrapper key={h.slug}>
                <TokenSkeleton />
              </TokenWrapper>
            );
          }

          if (!item) return null;

          return (
            <TokenWrapper key={h.slug}>
              <Token
                card={item}
                onlyPrimary
                hideSorareUser
                onInfoClick={() => {
                  track?.({
                    target: ClickMarketHomeSection_Target.PLAYER_DETAILS,
                    playerSlug: item.anyPlayer.slug,
                    cardSlug: item.slug,
                    offerId: item.latestEnglishAuction?.id,
                  });
                  setCardOpened({
                    cardSlug: item.slug,
                    playerSlug: item.anyPlayer.slug,
                  });
                }}
              />
            </TokenWrapper>
          );
        })}
      </Row>
      <PlayerDrawer
        cardOpened={cardOpened}
        onClose={() => {
          setCardOpened(null);
        }}
      />
    </SlideShow>
  );
};
